import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  UserCheck,
  UserX,
  KeyRound,
  Search,
  Shield,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { UserForm } from "./UserForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  permission_level: string;
  is_active: number;
  mfa_enabled: number;
  created_at: string;
}

const PERMISSION_LABELS: Record<string, string> = {
  BASIC: "Basic",
  FULL_ACCESS: "Full Access",
  WIZARD: "Wizard",
  AGENCY_ADMIN: "Agency Admin",
  SYSTEM_ADMIN: "System Admin",
  VIEW_ONLY: "View Only",
  CUSTOM: "Custom",
  BLOCKED: "Blocked",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UserManagement() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const { data, isLoading } = useQuery<{ data: User[]; meta: { total: number } }>({
    queryKey: ["admin-users", searchTerm, showActiveOnly],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (searchTerm) params.search = searchTerm;
      if (showActiveOnly) params.active = "true";
      return api.get("/admin/users", { params });
    },
  });

  const users = data?.data ?? [];

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/admin/users/${userId}/deactivate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/admin/users/${userId}/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      api.post(`/admin/users/${userId}/reset-password`, { newPassword }),
    onSuccess: () => {
      alert("Password has been reset successfully.");
    },
  });

  function handleResetPassword(user: User) {
    const newPassword = prompt(
      `Enter new password for ${user.first_name} ${user.last_name} (min 12 characters):`,
    );
    if (newPassword && newPassword.length >= 12) {
      resetPasswordMutation.mutate({ userId: user.id, newPassword });
    } else if (newPassword) {
      alert("Password must be at least 12 characters.");
    }
  }

  function handleFormSuccess() {
    setShowForm(false);
    setEditingUser(null);
    queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">User Management</h2>
          <p className="text-sm text-muted-foreground">
            Create, edit, and manage user accounts
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            setEditingUser(null);
            setShowForm(true);
          }}
        >
          <Plus className="h-4 w-4" />
          New User
        </Button>
      </div>

      {/* Search and filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background py-1 pl-9 pr-3 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            className="rounded"
          />
          Active only
        </label>
      </div>

      {/* User form dialog */}
      {showForm && (
        <UserForm
          user={editingUser}
          onClose={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {/* User list */}
      {!isLoading && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Email</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Role</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">MFA</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No users found.
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <tr key={user.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                        <Shield className="h-3 w-3" />
                        {PERMISSION_LABELS[user.permission_level] ?? user.permission_level}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                          user.mfa_enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600",
                        )}
                      >
                        {user.mfa_enabled ? "Enabled" : "Off"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                          user.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800",
                        )}
                      >
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingUser(user);
                            setShowForm(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Reset Password"
                          onClick={() => handleResetPassword(user)}
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                        </Button>
                        {user.is_active ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Deactivate"
                            onClick={() => {
                              if (confirm(`Deactivate ${user.first_name} ${user.last_name}?`)) {
                                deactivateMutation.mutate(user.id);
                              }
                            }}
                          >
                            <UserX className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Activate"
                            onClick={() => activateMutation.mutate(user.id)}
                          >
                            <UserCheck className="h-3.5 w-3.5 text-green-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {data?.meta && (
        <p className="text-sm text-muted-foreground">
          Showing {users.length} of {data.meta.total} users
        </p>
      )}
    </div>
  );
}
